import React, { useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Loader2, Info, UserMinus } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Badge } from "src/components/ui/badge";
import { Switch } from "src/components/ui/switch";
import { Label } from "src/components/ui/label";
import { Alert, AlertDescription } from "src/components/ui/alert";

// Updated color scheme and categories
const ACCEPTANCE_CATEGORIES = {
  highAcceptance: {
    name: "Absolútny súhlas",
    range: [5, 5],
    color: "#22c55e", // Green
    description: "Absolútny súhlas (5 bodov)",
  },
  mediumAcceptance: {
    name: "Súhlas s výhradami",
    range: [3, 4],
    color: "#eab308", // Yellow
    description: "Súhlas s výhradami (3-4 body)",
  },
  lowAcceptance: {
    name: "Nesúhlas",
    range: [1, 2],
    color: "#ef4444", // Red
    description: "Nesúhlas (1-2 body)",
  },
  noAcceptance: {
    name: "Absolútny nesúhlas",
    range: [0, 0],
    color: "#1f2937", // Dark gray (almost black)
    description: "Absolútny nesúhlas (0 bodov)",
  },
};

// Convert to format used by the pie chart
const COLORS = {
  "Absolútny súhlas": ACCEPTANCE_CATEGORIES.highAcceptance.color,
  "Súhlas s výhradami": ACCEPTANCE_CATEGORIES.mediumAcceptance.color,
  Nesúhlas: ACCEPTANCE_CATEGORIES.lowAcceptance.color,
  "Absolútny nesúhlas": ACCEPTANCE_CATEGORIES.noAcceptance.color,
};

const AcceptanceDistribution = ({
  evaluationData,
  loading = false,
  error = null,
  hideHeader = false,
}) => {
  const [excludeSelfAssessment, setExcludeSelfAssessment] = useState(false);

  // Process data from the parent component
  const { chartData, metrics } = useMemo(() => {
    // Initialize with default empty values
    const defaultResult = {
      chartData: [],
      metrics: {
        acceptanceCoefficient: 0,
        mediumAcceptanceCoefficient: 0,
        lowAcceptanceCoefficient: 0,
        noAcceptanceCoefficient: 0,
        totalEvaluations: 0,
      },
    };

    // Return early if no data available
    if (
      !evaluationData ||
      !evaluationData.solutions ||
      evaluationData.solutions.length === 0
    ) {
      return defaultResult;
    }

    const evaluationCounts = {
      highAcceptance: 0,
      mediumAcceptance: 0,
      lowAcceptance: 0,
      noAcceptance: 0,
    };

    const usersByCategory = {
      highAcceptance: new Set(),
      mediumAcceptance: new Set(),
      lowAcceptance: new Set(),
      noAcceptance: new Set(),
    };

    let totalEvaluations = 0;

    // Process each solution and its evaluations
    evaluationData.solutions.forEach((solution) => {
      let evaluationsToProcess = solution.evaluations || [];

      // Filter out self-evaluations if excludeSelfAssessment is true
      if (excludeSelfAssessment) {
        evaluationsToProcess = evaluationsToProcess.filter(
          (evaluation) => evaluation.evaluator_id !== solution.user_id
        );
      }

      evaluationsToProcess.forEach((evaluation) => {
        totalEvaluations++;
        const evaluatorName = evaluation.users
          ? `${evaluation.users.first_name} ${evaluation.users.last_name}`
          : "Unknown";

        const score = evaluation.score;

        // Categorize based on the score
        if (score === 5) {
          evaluationCounts.highAcceptance++;
          usersByCategory.highAcceptance.add(evaluatorName);
        } else if (score >= 3 && score <= 4) {
          evaluationCounts.mediumAcceptance++;
          usersByCategory.mediumAcceptance.add(evaluatorName);
        } else if (score >= 1 && score <= 2) {
          evaluationCounts.lowAcceptance++;
          usersByCategory.lowAcceptance.add(evaluatorName);
        } else if (score === 0) {
          evaluationCounts.noAcceptance++;
          usersByCategory.noAcceptance.add(evaluatorName);
        }
      });
    });

    // Calculate coefficients
    const calculatedMetrics = {
      acceptanceCoefficient:
        totalEvaluations > 0
          ? evaluationCounts.highAcceptance / totalEvaluations
          : 0,
      mediumAcceptanceCoefficient:
        totalEvaluations > 0
          ? evaluationCounts.mediumAcceptance / totalEvaluations
          : 0,
      lowAcceptanceCoefficient:
        totalEvaluations > 0
          ? evaluationCounts.lowAcceptance / totalEvaluations
          : 0,
      noAcceptanceCoefficient:
        totalEvaluations > 0
          ? evaluationCounts.noAcceptance / totalEvaluations
          : 0,
      totalEvaluations,
    };

    // Format data for the chart
    const formattedChartData = [
      {
        name: "Absolútny súhlas",
        value: evaluationCounts.highAcceptance,
        users: Array.from(usersByCategory.highAcceptance),
      },
      {
        name: "Súhlas s výhradami",
        value: evaluationCounts.mediumAcceptance,
        users: Array.from(usersByCategory.mediumAcceptance),
      },
      {
        name: "Nesúhlas",
        value: evaluationCounts.lowAcceptance,
        users: Array.from(usersByCategory.lowAcceptance),
      },
      {
        name: "Absolútny nesúhlas",
        value: evaluationCounts.noAcceptance,
        users: Array.from(usersByCategory.noAcceptance),
      },
    ];

    return {
      chartData: formattedChartData,
      metrics: calculatedMetrics,
    };
  }, [evaluationData, excludeSelfAssessment]);

  const renderColorfulLegendText = (value) => {
    return <span className="text-sm font-medium text-gray-700">{value}</span>;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value, users } = payload[0].payload;
      return (
        <div className="bg-white p-4 border rounded-lg shadow-lg max-w-xs">
          <div className="font-medium text-lg mb-2 flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: COLORS[name] }}
            />
            {name}
          </div>
          <div className="text-sm text-gray-600 mb-2">
            Počet hodnotení: {value}
            <br />
            Podiel:{" "}
            {metrics.totalEvaluations > 0
              ? ((value / metrics.totalEvaluations) * 100).toFixed(1)
              : 0}
            %
          </div>
          {users.length > 0 && (
            <div className="mt-2">
              <div className="font-medium mb-1">Hodnotitelia:</div>
              <div className="max-h-40 overflow-y-auto">
                <ul className="list-disc pl-4 space-y-1">
                  {users.map((user, index) => (
                    <li key={index} className="text-gray-700">
                      {user}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Handle the case when no data is available
  const noData = !chartData.length || metrics.totalEvaluations === 0;

  const emptyStateContent = (
    <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg">
      <p className="text-gray-500">Žiadne dáta o akceptácii nie sú dostupné</p>
    </div>
  );

  if (noData) {
    // If hideHeader is true, just return the empty state without card
    if (hideHeader) {
      return emptyStateContent;
    }

    // Otherwise return empty state with card structure
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center space-x-2">
            <CardTitle>Distribúcia akceptácie</CardTitle>
            <HoverCard>
              <HoverCardTrigger asChild>
                <Info className="h-4 w-4 text-gray-500 cursor-help" />
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="space-y-2">
                  <h4 className="font-semibold">Ako sa počítajú kategórie?</h4>
                  {/* Category explanations */}
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
        </CardHeader>
        <CardContent>{emptyStateContent}</CardContent>
      </Card>
    );
  }

  const content = (
    <div className="h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="45%"
            innerRadius={60}
            outerRadius={140}
            fill="#8884d8"
            paddingAngle={5}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.name]}
                className="hover:opacity-90 transition-opacity"
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={renderColorfulLegendText}
            iconType="circle"
            iconSize={10}
            wrapperStyle={{
              paddingTop: "0px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );

  if (hideHeader) {
    return content;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center space-x-2">
          <CardTitle>Distribúcia akceptácie</CardTitle>
          <HoverCard>
            <HoverCardTrigger asChild>
              <Info className="h-4 w-4 text-gray-500 cursor-help" />
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-semibold">Ako sa počítajú kategórie?</h4>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        ACCEPTANCE_CATEGORIES.highAcceptance.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Absolútny súhlas</strong>: 5 bodov
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        ACCEPTANCE_CATEGORIES.mediumAcceptance.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Súhlas s výhradami</strong>: 3-4 body
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        ACCEPTANCE_CATEGORIES.lowAcceptance.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Nesúhlas</strong>: 1-2 body
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: ACCEPTANCE_CATEGORIES.noAcceptance.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Absolútny nesúhlas</strong>: 0 bodov
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>

        {/* Filter and metrics row */}
        <div className="flex flex-wrap items-center mt-3 gap-3">
          <div className="flex items-center space-x-2 bg-gray-50 p-1.5 px-3 rounded-full">
            <Switch
              id="exclude-self-assessment"
              checked={excludeSelfAssessment}
              onCheckedChange={setExcludeSelfAssessment}
              className="data-[state=checked]:bg-blue-600"
            />
            <Label
              htmlFor="exclude-self-assessment"
              className="cursor-pointer flex items-center text-sm font-medium"
            >
              <UserMinus className="h-3.5 w-3.5 mr-1.5" />
              Bez sebahodnotenia
            </Label>
          </div>

          <Badge variant="secondary" className="bg-blue-50 text-blue-700">
            {metrics.totalEvaluations} hodnotení
          </Badge>

          <Badge
            variant="secondary"
            className={`${
              metrics.acceptanceCoefficient >= 0.7
                ? "bg-green-50 text-green-700"
                : metrics.acceptanceCoefficient >= 0.5
                ? "bg-yellow-50 text-yellow-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {(metrics.acceptanceCoefficient * 100).toFixed(1)}% akceptácia
          </Badge>
        </div>
      </CardHeader>

      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default AcceptanceDistribution;
